import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
} from '@mui/material'
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { xxllncColor } from '../../layout/themes'
import { statusColor } from '../Research/ResearchPanelStyles'

export interface Case {
  id: string;
  case_number?: string;
  case_type?: string;
  subject?: string;
  status: string;
  created_at: string | null;
  title?: string;
}

export const formatCaseLabel = (c: Case): string => {
  if (c.case_number && !String(c.case_number).includes('-'))
    return `Zaak #${c.case_number}`
  return 'Nieuwe Zaak'
}

interface CaseLinkSectionProps {
  caseSearch: string;
  setCaseSearch: (value: string) => void;
  handleCaseSearch: () => void;
  caseResults: Case[];
  showCaseResults: boolean;
  internalCaseUuids: string[];
  handleSelectCase: (c: Case) => void;
  isDisabled: boolean;
  onUnlinkCase: (id?: string) => void;
  selectedCases?: Case[] | null;
  caseUuid?: string | null;
}

const MAX_VISIBLE = 5

export const CaseLinkSection = ({
  caseSearch,
  setCaseSearch,
  handleCaseSearch,
  caseResults,
  showCaseResults,
  internalCaseUuids = [],
  handleSelectCase,
  isDisabled,
  onUnlinkCase,
  selectedCases,
  caseUuid,
}: CaseLinkSectionProps) => {
  const visibleResults = caseResults.slice(0, MAX_VISIBLE)

  const deelzaakUuids = internalCaseUuids.filter(
    (id) => !caseUuid || id !== caseUuid,
  )

  const displayCases: Case[] = []
  deelzaakUuids.forEach((id) => {
    const foundInSelected = selectedCases?.find((c) => c.id === id)
    if (foundInSelected) {
      displayCases.push(foundInSelected)
    } else {
      const foundInResults = caseResults.find((c) => c.id === id)
      if (foundInResults) {
        displayCases.push(foundInResults)
      }
    }
  })
  const heeftZichtbareDeelzaken = displayCases.length > 0

  return (
    <Box sx={{ mt: 1 }}>
      {heeftZichtbareDeelzaken && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              minHeight: '32px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon
                sx={{ color: xxllncColor.green100, fontSize: 18 }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: xxllncColor.green100,
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                Zaak gekoppeld
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {displayCases.map((displayCase) => (
              <Paper
                key={displayCase.id}
                elevation={0}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: '8px',
                  bgcolor: 'rgba(0,0,0,0.02)',
                  border: '1px solid #f0f0f0',
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', flex: 1, mr: 1 }}
                >
                  <DescriptionOutlinedIcon
                    sx={{
                      mr: 1.5,
                      color: xxllncColor.dark60,
                    }}
                  />
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700 }}
                        >
                          {formatCaseLabel(displayCase)}
                        </Typography>
                        <Chip
                          label={displayCase.status || 'nieuw'}
                          size="small"
                          sx={{
                            bgcolor: (
                              statusColor[displayCase.status] ||
                              statusColor.open
                            ).bg,
                            color: (
                              statusColor[displayCase.status] ||
                              statusColor.open
                            ).text,
                            height: 20,
                            fontSize: '0.65rem',
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      displayCase.subject || 'Laden van zaakdetails...'
                    }
                  />
                </Box>

                {!isDisabled && (
                  <IconButton
                    size="small"
                    onClick={() => onUnlinkCase(displayCase.id)}
                    sx={{
                      'color': xxllncColor.dark60,
                      'p': 0.5,
                      '&:hover': {
                        color: xxllncColor.veniceBlue,
                        bgcolor: 'rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {!heeftZichtbareDeelzaken && internalCaseUuids.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CheckCircleIcon sx={{ color: xxllncColor.green100, fontSize: 18 }} />
          <Typography
            variant="body2"
            sx={{ color: xxllncColor.green100, fontWeight: 500 }}
          >
            Zaak gekoppeld
          </Typography>
        </Box>
      )}

      {!isDisabled && (
        <>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              mt: internalCaseUuids.length > 0 ? 2 : 0,
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Koppel nog een zaak (optioneel)"
              value={caseSearch}
              onChange={(e) => setCaseSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void handleCaseSearch()}
              disabled={isDisabled}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
            />
            <Button
              variant="contained"
              onClick={() => void handleCaseSearch()}
              disabled={isDisabled}
              sx={{
                'bgcolor': xxllncColor.veniceBlue,
                'minWidth': '40px',
                'width': '40px',
                'height': '40px',
                'borderRadius': '50%',
                'color': '#fff',
                '&:hover': { bgcolor: xxllncColor.blue100 },
              }}
            >
              <SearchIcon fontSize="small" />
            </Button>
          </Box>

          {showCaseResults && (
            <Paper
              elevation={2}
              sx={{
                mt: 1,
                maxHeight: '250px',
                overflowY: 'auto',
                borderRadius: '8px',
              }}
            >
              <List dense>
                {visibleResults.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ p: 2, textAlign: 'center' }}
                  >
                    Geen zaken gevonden
                  </Typography>
                ) : (
                  visibleResults.map((c: Case) => {
                    if (internalCaseUuids.includes(c.id)) return null

                    const colors = statusColor[c.status] || statusColor.open
                    return (
                      <ListItem
                        key={c.id}
                        onClick={() => handleSelectCase(c)}
                        sx={{
                          'borderBottom': '1px solid #f0f0f0',
                          'py': 1.5,
                          'cursor': 'pointer',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                        }}
                      >
                        <DescriptionOutlinedIcon
                          sx={{
                            mr: 1.5,
                            color: xxllncColor.dark60,
                          }}
                        />
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                {formatCaseLabel(c)}
                              </Typography>
                              <Chip
                                label={c.status}
                                size="small"
                                sx={{
                                  bgcolor: colors.bg,
                                  color: colors.text,
                                  height: 20,
                                  fontSize: '0.65rem',
                                }}
                              />
                            </Box>
                          }
                          secondary={c.subject}
                        />
                      </ListItem>
                    )
                  })
                )}
              </List>
            </Paper>
          )}
        </>
      )}
    </Box>
  )
}
